import React, { useEffect, useRef } from 'react';
import logo from '../../assets/images/logo.jpeg';

const DataFlowDiagram = ({ isMobile }) => {
  const desktopLogoRef = useRef(null);
  const mobileLogoRef = useRef(null);
  
  // Add logo to the container elements
  useEffect(() => {
    const setupLogo = (ref) => {
      if (!ref.current) return;
      
      // Create a simple container to center the logo
      const container = document.createElement('div');
      container.style.width = '100%';
      container.style.height = '100%';
      container.style.display = 'flex';
      container.style.alignItems = 'center';
      container.style.justifyContent = 'center';
      container.style.backgroundColor = 'white';
      container.style.borderRadius = '50%';
      container.style.border = '2px solid rgba(37, 99, 235, 0.2)';
      container.style.boxShadow = '0 0 10px rgba(37, 99, 235, 0.2)';
      
      // Create an image element with enhanced styling
      const img = document.createElement('img');
      img.src = logo;
      img.alt = 'AUDIT-AI Logo';
      img.style.width = '85%'; 
      img.style.height = '85%';
      img.style.objectFit = 'contain';
      img.style.borderRadius = '50%';
      
      // Build the component hierarchy
      container.appendChild(img);
      
      // Clear any existing children
      while (ref.current.firstChild) {
        ref.current.removeChild(ref.current.firstChild);
      }
      
      ref.current.appendChild(container);
    };
    
    setupLogo(desktopLogoRef);
    setupLogo(mobileLogoRef);
  }, []);

  return (
    <div className="relative bg-gradient-to-br from-gray-900 via-blue-950 to-black rounded-xl shadow-2xl border border-blue-900/30 overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <svg width="100%" height="100%">
          <pattern id="grid-pattern-small" width="24" height="24" patternUnits="userSpaceOnUse">
            <path d="M 24 0 L 0 0 0 24" fill="none" stroke="rgba(59, 130, 246, 0.5)" strokeWidth="0.5" />
          </pattern>
          <rect width="100%" height="100%" fill="url(#grid-pattern-small)" />
        </svg>
      </div>
      
      
      
      {/* Data Flow Visualization with Three Clear Phases */}
      <div className="relative py-8 px-4 bg-gradient-to-br from-blue-950 via-gray-900 to-black">
        {/* Flow Header - Desktop Only */}
        <div className="hidden md:flex flex-col md:flex-row justify-between items-center mb-10 px-4 gap-4">
          <div className="md:flex-1 text-center">
            <div className="inline-flex items-center justify-center bg-blue-950/70 text-blue-300 px-4 py-2 rounded-full font-semibold backdrop-blur-sm border border-blue-800/50">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM6.293 6.707a1 1 0 010-1.414l3-3a1 1 0 011.414 0l3 3a1 1 0 01-1.414 1.414L11 5.414V13a1 1 0 11-2 0V5.414L7.707 6.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
              </svg>
              <span>DATA SOURCES</span>
            </div>
          </div>
          <div className="md:flex-1 text-center">
            <div className="inline-flex items-center justify-center bg-blue-900/70 text-blue-300 px-4 py-2 rounded-full font-semibold backdrop-blur-sm border border-blue-700/50">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
              </svg>
              <span>AI PROCESSING</span>
            </div>
          </div>
          <div className="md:flex-1 text-center">
            <div className="inline-flex items-center justify-center bg-blue-800/70 text-blue-300 px-4 py-2 rounded-full font-semibold backdrop-blur-sm border border-blue-600/50">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11.707 4.707a1 1 0 00-1.414-1.414L10 9.586 8.707 8.293a1 1 0 00-1.414 0l-2 2a1 1 0 101.414 1.414L8 10.414l1.293 1.293a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>STAKEHOLDERS & REPORTING</span>
            </div>
          </div>
        </div>
        
        {/* Main Flow Diagram - Desktop Only */}
        <div className="hidden md:flex flex-row items-stretch gap-4 mb-6 relative">
          {/* Phase 1: Data Sources */}
          <div className="flex-1 bg-gradient-to-br from-blue-950/90 to-gray-900/90 rounded-xl p-4 relative overflow-hidden border border-blue-900/40 backdrop-blur-sm">
            {/* HR System */}
            <div className="mb-6 bg-gradient-to-br from-blue-950/60 to-gray-900/60 rounded-lg p-4 shadow-lg border border-blue-800/30 relative overflow-hidden">
              <div className="flex items-center">
                <div className="bg-blue-900/50 rounded-full p-3 mr-3 backdrop-blur-sm">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h4 className="text-blue-300 font-bold text-lg">HR Information</h4>
                  <p className="text-blue-100/80 text-sm">Employee Data</p>
                </div>
              </div>
              <div className="absolute right-0 top-1/2 -translate-y-1/2 w-12 h-1">
                <div className="data-flow-line w-full h-full bg-gradient-to-r from-transparent via-blue-500 to-transparent opacity-80"></div>
              </div>
            </div>
            
            {/* ERP System */}
            <div className="bg-gradient-to-br from-blue-950/60 to-gray-900/60 rounded-lg p-4 shadow-lg border border-blue-800/30 relative overflow-hidden">
              <div className="flex items-center">
                <div className="bg-blue-900/50 rounded-full p-3 mr-3 backdrop-blur-sm">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h4 className="text-blue-300 font-bold text-lg">ERP Systems</h4>
                  <p className="text-blue-100/80 text-sm">Access Data</p>
                </div>
              </div>
              <div className="absolute right-0 top-1/2 -translate-y-1/2 w-12 h-1">
                <div className="data-flow-line w-full h-full bg-gradient-to-r from-transparent via-blue-500 to-transparent opacity-80"></div>
              </div>
            </div>
          </div>
          
          {/* Phase 2: AI Processing */}
          <div className="flex-1 bg-gradient-to-br from-blue-900/90 to-gray-900/90 rounded-xl p-4 relative overflow-hidden border border-blue-800/40 backdrop-blur-sm">
            {/* AUDIT-AI Platform */}
            <div className="h-full flex flex-col">
              <div className="flex-grow py-4 flex items-center justify-center">
                <div className="bg-gradient-to-br from-blue-900/60 to-gray-900/60 rounded-xl p-6 shadow-lg border border-blue-700/30 text-center max-w-xs relative">
                  <div className="absolute -left-4 top-1/3 data-flow-in-pulse"></div>
                  <div className="absolute -right-4 top-1/3 data-flow-out-pulse"></div>
                  
                  {/* Logo */}
                  <div className="w-20 h-20 mx-auto bg-blue-800/30 rounded-full p-2 mb-4 flex items-center justify-center relative backdrop-blur-sm data-processor-pulse">
                    <div ref={desktopLogoRef} className="w-16 h-16 rounded-full overflow-hidden"></div>
                  </div>
                  
                  <h3 className="text-blue-300 font-bold text-xl mb-2">AUDIT-AI</h3>
                  
                  
                  {/* Processing Animation */}
                  <div className="w-full bg-blue-900/30 h-2 rounded-full overflow-hidden mb-4 backdrop-blur-sm">
                    <div className="h-full bg-gradient-to-r from-blue-400 via-blue-500 to-blue-600 rounded-full animate-processing"></div>
                  </div>
                  
                  <div className="text-blue-100/80 text-sm">
                    AI Powered Processing & Analysis
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Phase 3: Stakeholders & Reporting */}
          <div className="flex-1 bg-gradient-to-br from-blue-800/90 to-gray-900/90 rounded-xl p-4 relative overflow-hidden border border-blue-700/40 backdrop-blur-sm">
            {/* Control Owners */}
            <div className="mb-6 bg-gradient-to-br from-blue-800/60 to-gray-900/60 rounded-lg p-4 shadow-lg border border-blue-700/30 relative overflow-hidden">
              <div className="flex items-center">
                <div className="bg-blue-800/50 rounded-full p-3 mr-3 backdrop-blur-sm">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h4 className="text-blue-300 font-bold text-lg">Control Owners</h4>
                  <p className="text-blue-100/80 text-sm">Compliance Reports</p>
                </div>
              </div>
              <div className="absolute left-0 top-1/2 -translate-y-1/2 w-12 h-1">
                <div className="data-flow-line w-full h-full bg-gradient-to-r from-blue-500 via-blue-500 to-transparent opacity-80"></div>
              </div>
            </div>
            
            {/* Audit Teams */}
            <div className="bg-gradient-to-br from-blue-800/60 to-gray-900/60 rounded-lg p-4 shadow-lg border border-blue-700/30 relative overflow-hidden">
              <div className="flex items-center">
                <div className="bg-blue-800/50 rounded-full p-3 mr-3 backdrop-blur-sm">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h4 className="text-blue-300 font-bold text-lg">Audit Teams</h4>
                  <p className="text-blue-100/80 text-sm">Audit Reports </p>
                </div>
              </div>
              <div className="absolute left-0 top-1/2 -translate-y-1/2 w-12 h-1">
                <div className="data-flow-line w-full h-full bg-gradient-to-r from-blue-500 via-blue-500 to-transparent opacity-80"></div>
              </div>
            </div>
          </div>
          
          {/* Data Direction Arrows (z-index makes them appear on top) */}
          <div className="absolute left-1/3 top-1/2 transform -translate-y-1/2 -translate-x-1/2 z-20">
            <div className="bg-gradient-to-r from-blue-600 to-blue-500 text-white h-12 w-12 rounded-full flex items-center justify-center shadow-lg border border-blue-400/20 animate-slow-pulse">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </div>
          </div>
          
          <div className="absolute left-2/3 top-1/2 transform -translate-y-1/2 -translate-x-1/2 z-20">
            <div className="bg-gradient-to-r from-blue-500 to-blue-400 text-white h-12 w-12 rounded-full flex items-center justify-center shadow-lg border border-blue-400/20 animate-slow-pulse" style={{animationDelay: '0.5s'}}>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </div>
          </div>
        </div>
        
        {/* Mobile Flow Diagram - Vertical Layout */}
        <div className="md:hidden flex flex-col space-y-6">
          {/* Data Sources Section */}
          <div className="bg-gradient-to-br from-blue-950/90 to-gray-900/90 rounded-xl p-4 border border-blue-900/40 backdrop-blur-sm">
            <h4 className="text-blue-300 font-bold text-lg mb-3 text-center">Data Sources</h4>
            
            {/* HR System */}
            <div className="mb-4 bg-gradient-to-br from-blue-950/60 to-gray-900/60 rounded-lg p-3 shadow-lg border border-blue-800/30">
              <div className="flex items-center">
                <div className="bg-blue-900/50 rounded-full p-2 mr-3 backdrop-blur-sm">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h5 className="text-blue-300 font-bold text-base">HR Information</h5>
                  <p className="text-blue-100/80 text-xs">Employee Data · Access · Permissions</p>
                </div>
              </div>
            </div>
            
            {/* ERP System */}
            <div className="bg-gradient-to-br from-blue-950/60 to-gray-900/60 rounded-lg p-3 shadow-lg border border-blue-800/30">
              <div className="flex items-center">
                <div className="bg-blue-900/50 rounded-full p-2 mr-3 backdrop-blur-sm">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h5 className="text-blue-300 font-bold text-base">ERP Systems</h5>
                  <p className="text-blue-100/80 text-xs">SAP · Oracle · Dynamics · Financial Data</p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Down Arrow */}
          <div className="flex justify-center">
            <div className="bg-gradient-to-b from-blue-600 to-blue-500 text-white h-10 w-10 rounded-full flex items-center justify-center shadow-lg border border-blue-400/20 animate-slow-pulse">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
              </svg>
            </div>
          </div>
          
          {/* AI Processing */}
          <div className="bg-gradient-to-br from-blue-900/90 to-gray-900/90 rounded-xl p-4 border border-blue-800/40 backdrop-blur-sm">
            <h4 className="text-blue-300 font-bold text-lg mb-3 text-center">AI Processing</h4>
            
            <div className="bg-gradient-to-br from-blue-900/60 to-gray-900/60 rounded-xl p-4 shadow-lg border border-blue-700/30 flex flex-col items-center">
              {/* Logo */}
              <div className="w-16 h-16 bg-blue-800/30 rounded-full p-2 mb-3 flex items-center justify-center backdrop-blur-sm data-processor-pulse">
                <div ref={mobileLogoRef} className="w-12 h-12 rounded-full overflow-hidden"></div>
              </div>
              
              <h5 className="text-blue-300 font-bold text-base mb-1">AUDIT-AI</h5>
              <div className="flex justify-between w-full text-xs text-blue-100/80 font-medium mb-3">
                <span>Data Ingestion</span>
                <span>AI Analysis</span>
                <span>Compliance</span>
              </div>
              
              {/* Processing Animation */}
              <div className="w-full bg-blue-900/30 h-2 rounded-full overflow-hidden mb-3 backdrop-blur-sm">
                <div className="h-full bg-gradient-to-r from-blue-400 via-blue-500 to-blue-600 rounded-full animate-processing"></div>
              </div>
              
              <p className="text-blue-100/80 text-xs">
                Automated Processing & Analysis
              </p>
            </div>
          </div>
          
          {/* Down Arrow */}
          <div className="flex justify-center">
            <div className="bg-gradient-to-b from-blue-500 to-blue-400 text-white h-10 w-10 rounded-full flex items-center justify-center shadow-lg border border-blue-400/20 animate-slow-pulse" style={{animationDelay: '0.5s'}}>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
              </svg>
            </div>
          </div>
          
          {/* Stakeholders & Reporting */}
          <div className="bg-gradient-to-br from-blue-800/90 to-gray-900/90 rounded-xl p-4 border border-blue-700/40 backdrop-blur-sm">
            <h4 className="text-blue-300 font-bold text-lg mb-3 text-center">Stakeholders & Reporting</h4>
            
            {/* Control Owners */}
            <div className="mb-4 bg-gradient-to-br from-blue-800/60 to-gray-900/60 rounded-lg p-3 shadow-lg border border-blue-700/30">
              <div className="flex items-center">
                <div className="bg-blue-800/50 rounded-full p-2 mr-3 backdrop-blur-sm">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h5 className="text-blue-300 font-bold text-base">Control Owners</h5>
                  <p className="text-blue-100/80 text-xs">System Admins · View Violations · Remediate</p>
                </div>
              </div>
            </div>
            
            {/* Audit Teams */}
            <div className="bg-gradient-to-br from-blue-800/60 to-gray-900/60 rounded-lg p-3 shadow-lg border border-blue-700/30">
              <div className="flex items-center">
                <div className="bg-blue-800/50 rounded-full p-2 mr-3 backdrop-blur-sm">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h5 className="text-blue-300 font-bold text-base">Audit Teams</h5>
                  <p className="text-blue-100/80 text-xs">Testing · Reports · Findings · Downloads</p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Animated Connection Line */}
          <div className="absolute left-1/2 top-0 bottom-0 w-0.5 -z-10">
            <div className="h-full bg-gradient-to-b from-transparent via-blue-500 to-transparent opacity-20"></div>
          </div>
        </div>
      </div>
      
      {/* CSS Animations */}
      <style jsx>{`
        @keyframes slowPulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.1); opacity: 0.8; }
        }
        
        @keyframes processing {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        
        @keyframes flowLineAnimation {
          0% { transform: scaleX(0); opacity: 0; }
          50% { transform: scaleX(1); opacity: 1; }
          100% { transform: scaleX(0); opacity: 0; }
        }
        
        @keyframes pulseEffect {
          0% { box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.7); }
          70% { box-shadow: 0 0 0 10px rgba(59, 130, 246, 0); }
          100% { box-shadow: 0 0 0 0 rgba(59, 130, 246, 0); }
        }
        
        .animate-slow-pulse {
          animation: slowPulse 3s infinite ease-in-out;
        }
        
        .animate-processing {
          animation: processing 2s infinite linear;
        }
        
        .data-flow-line {
          animation: flowLineAnimation 2s infinite ease-in-out;
        }
        
        .data-processor-pulse {
          animation: pulseEffect 2s infinite;
        }
        
        .data-flow-in-pulse::before,
        .data-flow-out-pulse::before {
          content: '';
          position: absolute;
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background-color: #3b82f6;
          animation: pulseEffect 2s infinite;
        }
      `}</style>
    </div>
  );
};

export default DataFlowDiagram;

